import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { BiSolidDownArrow, BiSolidRightArrow } from "react-icons/bi";
import countries from "./countries.json";
import { useCountry } from '../pages/CountryContext';
import { motion, AnimatePresence } from "framer-motion";

const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [hoveredItem, setHoveredItem] = useState(null);
  const [subDropdownOpen, setSubDropdownOpen] = useState(null);
  const { selectedCountry = { name: "South Africa", flag: "https://flagcdn.com/w45/za.png" }, 
         setSelectedCountry = () => {} } = useCountry() || {};
  const chevronRef = useRef(null);
  const [flipped, setFlipped] = useState(false);
  const navRef = useRef(null);
  const hoverTransition = "transition-all duration-300 ease-in-out";
  const [activeBottomDropdown, setActiveBottomDropdown] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [showNotifications, setShowNotifications] = useState(false);
  const mobileMenuRef = useRef(null);
  
  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
      if (navRef.current && !navRef.current.contains(event.target)) {
        setHoveredItem(null);
        setSubDropdownOpen(null);
        setActiveBottomDropdown(null);
        setShowNotifications(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const notificationItems = [
    { key: 'item0', label: 'Diseases' },
    { key: 'item1', label: 'Pests' },
    { key: 'item2', label: 'Support' },
    { key: 'item3', label: 'Market' },
    { key: 'item4', label: 'Community' },
    { key: 'item5', label: 'Weather' },
    { key: 'item6', label: 'Partners' },
    { key: 'item7', label: 'Government' },
  ];

  const handleInProgressClick = (pageName) => {
    setPopupMessage(`Our ${pageName} section is currently being upgraded. Stay tuned for updates!`);
    setShowPopup(true);
    setTimeout(() => setShowPopup(false), 6000);
  };

  const menuItems = [
    { key: "About", label: "About Us", path: "/About" },
    { key: "FarmTool", label: "Farm Tool", path: "/farm-tool" },
    { key: "Resources", label: "Resources", path: "/resources" },
    { key: "Communication", label: "Communication", path: "/communication" },
  ];

  const handleNavigation = (direction) => {
    if (direction === "next") {
      window.history.forward();
    } else {
      window.history.back();
    }
  };
  
  useEffect(() => {
    const currentPath = location.pathname;
  
    if (currentPath.startsWith("/reports")) {
      setActiveTab("Reports");
    } else if (currentPath.startsWith("/farm-tool")) {
      setActiveTab("FarmTool");
    } else if (currentPath.startsWith("/resources")) {
      setActiveTab("Resources");
    } else if (currentPath.startsWith("/communication")) {
      setActiveTab("Communication");
    } else {
      setActiveTab("");
    }
  }, [location.pathname]);
  
  const handleCountryChange = (country) => {
    const fullCountryData = countries.find(c => c.name === country.name);
    setSelectedCountry(fullCountryData);
    setDropdownOpen(false);
    localStorage.setItem('selectedCountry', JSON.stringify(fullCountryData));
    navigate(`?country=${encodeURIComponent(fullCountryData.name)}`);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (navRef.current && !navRef.current.contains(event.target)) {
        setHoveredItem(null);
        setSubDropdownOpen(null);
        setActiveBottomDropdown(null);
        setShowNotifications(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const toggleSubDropdown = (itemKey, e) => {
    if (e) e.preventDefault();
    setSubDropdownOpen(subDropdownOpen === itemKey ? null : itemKey);
    setHoveredItem(itemKey);
  };

  const renderNavItem = (item, isMobile = false) => (
    <li
      key={item.key}
      className={`
      text-center h-[20px] md:h-[40px] flex items-center justify-center relative
      border-b-2 bg-[#2E453D] md:rounded-[2px] rounded-[2px] shadow-black shadow-md
      ${activeTab === item.key ? "border-[#FDBD26] text-[#FDBD26]" : 
         item.key === "About" ? "border-[#FDBD26] text-white" : // Make first item border yellow
         "border-white text-white"}
      ${
        ["home", "reports", "services", "gallery"].includes(item.key)
          ? "w-[120px] xl:w-[150px] lg:w-[100px]"
          : "w-[50px] xl:w-[190px] lg:w-[160px]"
      }
    `}
      onMouseEnter={!isMobile ? () => setHoveredItem(item.key) : undefined}
      onMouseLeave={!isMobile ? () => setHoveredItem(null) : undefined}
      onClick={isMobile ? () => {
        setHoveredItem(hoveredItem === item.key ? null : item.key);
      } : undefined}
    >
      <Link
        to={item.path}
        onClick={(e) => {
          setActiveTab(item.key);
        }}
        className="block text-[6px] lg:text-[10px] xl:text-[14px] w-full text-center font-bold relative"
      >
        {item.label}
        {isMobile && (
          <BiSolidRightArrow className="absolute right-1 top-1/2 transform -translate-y-1/2 text-[8px]" />
        )}
      </Link>
      
      {/* Desktop Dropdown Content */}
      {hoveredItem === item.key && !isMobile && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.2 }}
          className={`absolute top-full left-0 w-full xl:w-[190px] lg:w-[160px] z-50`}
          onMouseEnter={() => setHoveredItem(item.key)}
          onMouseLeave={() => setHoveredItem(null)}
        >
          <div className="-mt-2">
            <div className={`absolute top-full left-0 w-full xl:w-[190px] lg:w-[100px]`}>
              <div className="b">
                <div className="py-1">
                  <ul className="space-y-2">
                    
                    {/* REPORTS Dropdown */}
                    {item.key === "About" && (
                      <>
                        
                        <li className="group flex items-center justify-between">
                          <a href="/report" className="block text-white text-[8px] md:text-[14px] py-1 px-1 w-full h-[20px] xl:w-[190px] lg:w-[100px] border-b-2 border-white h-[35px] md:h-[45px] flex justify-center items-center shadow shadow-xl bg-[#2E453D] rounded-[2px] duration-200">
                            Reports
                          </a>
                        </li>
                        <li className="group flex items-center justify-between">
                          <a href="/news" className="block text-white text-[8px] md:text-[14px] py-1 px-1 w-full h-[20px] xl:w-[190px] lg:w-[100px] border-b-2 border-white h-[35px] md:h-[45px] flex justify-center items-center shadow shadow-xl bg-[#2E453D] rounded-[2px] duration-200">
                            News
                          </a>
                        </li>
                      </>
                    )}

                    {/* FARM TOOL Dropdown */}
                    {item.key === "FarmTool" && (
                      <>
                        <li className="group flex items-center justify-between">
                          <a href="/Catalogue" className="block text-white text-[8px] md:text-[14px] py-1 px-1 w-full xl:w-[190px] flex justify-center items-center shadow-xl h-[20px] md:h-[45px] lg:w-[100px] border-b-2 border-white bg-[#2E453D] rounded-[2px] duration-200">
                            Catalogue
                          </a>
                        </li>
                        <li className="group flex items-center justify-between">
                          <a href="/farm-tool/website" className="block text-white text-[8px] md:text-[14px] py-1 px-1 w-full h-[20px] xl:w-[190px] lg:w-[100px] border-b-2 border-white h-[35px] md:h-[45px] flex justify-center items-center shadow shadow-xl bg-[#2E453D] rounded-[2px] duration-200">
                            Website
                          </a>
                        </li>
                        <li className="group flex items-center justify-between">
                          <a href="/farm-tool/crm" className="block text-white text-[8px] md:text-[14px] py-1 px-1 w-full h-[20px] xl:w-[190px] lg:w-[100px] border-b-2 border-white h-[35px] md:h-[45px] flex justify-center items-center shadow shadow-xl bg-[#2E453D] rounded-[2px] duration-200">
                            CRM
                          </a>
                        </li>
                        <li className="group flex items-center justify-between">
                          <a href="/farm-tool/simulator" className="block text-white text-[8px] md:text-[14px] py-1 px-1 w-full h-[20px] xl:w-[190px] lg:w-[100px] border-b-2 border-white h-[35px] md:h-[45px] flex justify-center items-center shadow shadow-xl bg-[#2E453D] rounded-[2px] duration-200">
                            Simulator
                          </a>
                        </li>
                      </>
                    )}

                    {/* RESOURCES Dropdown */}
                    {item.key === "Resources" && (
                      <>
                        <li className="group flex items-center justify-between">
                          <a href="/resources/trainings" className="block text-white text-[8px] md:text-[14px] py-1 px-1 w-full xl:w-[190px] flex justify-center items-center shadow-xl h-[20px] md:h-[45px] lg:w-[100px] border-b-2 border-white bg-[#2E453D] rounded-[2px] duration-200">
                            Trainings & Learning
                          </a>
                        </li>
                        <li className="group flex items-center justify-between">
                          <a href="/resources/finance" className="block text-white text-[8px] md:text-[14px] py-1 px-1 w-full h-[20px] xl:w-[190px] lg:w-[100px] border-b-2 border-white h-[35px] md:h-[45px] flex justify-center items-center shadow shadow-xl bg-[#2E453D] rounded-[2px] duration-200">
                            Finance
                          </a>
                        </li>
                        <li className="group flex items-center justify-between">
                          <a href="/resources/guides" className="block text-white text-[8px] md:text-[14px] py-1 px-1 w-full h-[20px] xl:w-[190px] lg:w-[100px] border-b-2 border-white h-[35px] md:h-[45px] flex justify-center items-center shadow shadow-xl bg-[#2E453D] rounded-[2px] duration-200">
                            Guides/Instructions
                          </a>
                        </li>
                        <li className="group flex items-center justify-between">
                          <a href="/resources/programs" className="block text-white text-[8px] md:text-[14px] py-1 px-1 w-full h-[20px] xl:w-[190px] lg:w-[100px] border-b-2 border-white h-[35px] md:h-[45px] flex justify-center items-center shadow shadow-xl bg-[#2E453D] rounded-[2px] duration-200">
                            Programs
                          </a>
                        </li>
                        <li className="group flex items-center justify-between">
                          <a href="/resources/schedules" className="block text-white text-[8px] md:text-[14px] py-1 px-1 w-full h-[20px] xl:w-[190px] lg:w-[100px] border-b-2 border-white h-[35px] md:h-[45px] flex justify-center items-center shadow shadow-xl bg-[#2E453D] rounded-[2px] duration-200">
                            Schedules
                          </a>
                        </li>
                        <li className="group flex items-center justify-between">
                          <a href="/resources/development" className="block text-white text-[8px] md:text-[14px] py-1 px-1 w-full h-[20px] xl:w-[190px] lg:w-[100px] border-b-2 border-white h-[35px] md:h-[45px] flex justify-center items-center shadow shadow-xl bg-[#2E453D] rounded-[2px] duration-200">
                            Development
                          </a>
                        </li>
                      </>
                    )}

                    {/* COMMUNICATION Dropdown */}
                    {item.key === "Communication" && (
                      <>
                        <li className="group flex items-center justify-between">
                          <a href="/communication/impact" className="block text-white text-[8px] md:text-[14px] py-1 px-1 w-full xl:w-[190px] flex justify-center items-center shadow-xl h-[20px] md:h-[45px] lg:w-[100px] border-b-2 border-white bg-[#2E453D] rounded-[2px] duration-200">
                            Impact
                          </a>
                        </li>
                        <li className="group flex items-center justify-between">
                          <a href="/communication/environmental" className="block text-white text-[8px] md:text-[14px] py-1 px-1 w-full h-[20px] xl:w-[190px] lg:w-[100px] border-b-2 border-white h-[35px] md:h-[45px] flex justify-center items-center shadow shadow-xl bg-[#2E453D] rounded-[2px] duration-200">
                            Environmental (ESG)
                          </a>
                        </li>
                        <li className="group flex items-center justify-between">
                          <a href="/communication/social" className="block text-white text-[8px] md:text-[14px] py-1 px-1 w-full h-[20px] xl:w-[190px] lg:w-[100px] border-b-2 border-white h-[35px] md:h-[45px] flex justify-center items-center shadow shadow-xl bg-[#2E453D] rounded-[2px] duration-200">
                            Social (ESG)
                          </a>
                        </li>
                        <li className="group flex items-center justify-between">
                          <a href="/communication/governance" className="block text-white text-[8px] md:text-[14px] py-1 px-1 w-full h-[20px] xl:w-[190px] lg:w-[100px] border-b-2 border-white h-[35px] md:h-[45px] flex justify-center items-center shadow shadow-xl bg-[#2E453D] rounded-[2px] duration-200">
                            Governance (ESG)
                          </a>
                        </li>
                      </>
                    )}

                  </ul>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </li>
  );

  const MobileMenuToggle = () => (
    <button 
      className="flex items-center gap-1 cursor-pointer z-50 p-2"
      onClick={() => setMenuOpen(!menuOpen)}
      aria-label="Toggle menu"
    >
      {menuOpen ? (
        <span className="text-white text-xl">×</span>
      ) : (
        <>
          <div className="flex flex-col justify-between h-3 w-4">
            <span className="w-full h-[2px] bg-white rounded-sm"></span>
            <span className="w-full h-[2px] bg-white rounded-sm"></span>
          </div>
          <span className="text-white text-[10px]">Menu</span>
        </>
      )}
    </button>
  );

  const renderMobileNavItem = (item) => (
    <li
      key={item.key}
      className="relative flex"
      onClick={(e) => {
        e.stopPropagation();
        setHoveredItem(hoveredItem === item.key ? null : item.key);
      }}
    >
      {/* Main Nav Item */}
      <div className={`
        flex items-center justify-between w-[120px] px-4 py-2 rounded-[2px] 
        ${hoveredItem === item.key ? 'bg-[#3a5a4a] text-[#FDBD26]' : 'bg-[#2E453D] text-white'}
        border-b-2 ${item.key === "Reports" ? "border-[#FDBD26]" : "border-white"}
      `}>
        <span className="text-[10px] font-medium">{item.label}</span>
        <img src="https://res.cloudinary.com/dwfdzdaxk/image/upload/v1753988748/Expand_right_light_dn8ljr.png" alt="icon" className={`w-[12px] h-[12px] ${hoveredItem === item.key ? 'rotate-90' : ''}`}/>
      </div>
      
      {/* Mobile Dropdown Items */}
      {hoveredItem === item.key && (
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          className="absolute left-[118px] top-0 ml-1 rounded-r-lg shadow-lg z-50"
          style={{ width: '120px' }}
        >
          <div className="px-1">
            <ul className="space-y-0.5">
              
              {/* REPORTS Mobile Dropdown */}
              {item.key === "Reports" && (
                <>
                  <li className="group flex items-center justify-between">
                    <a href="/reports/about" className="block text-white text-[8px] h-[20px] py-1 px-1 w-full flex justify-center items-center shadow-xl h-[40px] border-b-2 border-black bg-[#2E453D] rounded-[2px] duration-200">
                      About
                    </a>
                  </li>
                  <li className="group flex items-center justify-between">
                    <a href="/reports" className="block text-white text-[8px] py-1 px-1 w-full h-[20px] border-b-2 border-black h-[35px] flex justify-center items-center shadow shadow-xl bg-[#2E453D] rounded-[2px] duration-200">
                      Reports
                    </a>
                  </li>
                  <li className="group flex items-center justify-between">
                    <a href="/news" className="block text-white text-[8px] py-1 px-1 w-full h-[20px] border-b-2 border-black h-[35px] flex justify-center items-center shadow shadow-xl bg-[#2E453D] rounded-[2px] duration-200">
                      News
                    </a>
                  </li>
                </>
              )}

              {/* FARM TOOL Mobile Dropdown */}
              {item.key === "FarmTool" && (
                <>
                  <li className="group flex items-center justify-between">
                    <a href="/farm-tool/catalogue" className="block text-white text-[8px] py-1 px-1 w-full h-[20px] border-b-2 border-black h-[35px] flex justify-center items-center shadow shadow-xl bg-[#2E453D] rounded-[2px] duration-200">
                      Catalogue
                    </a>
                  </li>
                  <li className="group flex items-center justify-between">
                    <a href="/farm-tool/website" className="block text-white text-[8px] py-1 px-1 w-full h-[20px] border-b-2 border-black h-[35px] flex justify-center items-center shadow shadow-xl bg-[#2E453D] rounded-[2px] duration-200">
                      Website
                    </a>
                  </li>
                  <li className="group flex items-center justify-between">
                    <a href="/farm-tool/crm" className="block text-white text-[8px] py-1 px-1 w-full h-[20px] border-b-2 border-black h-[35px] flex justify-center items-center shadow shadow-xl bg-[#2E453D] rounded-[2px] duration-200">
                      CRM
                    </a>
                  </li>
                  <li className="group flex items-center justify-between">
                    <a href="/farm-tool/simulator" className="block text-white text-[8px] py-1 px-1 w-full h-[20px] border-b-2 border-black h-[35px] flex justify-center items-center shadow shadow-xl bg-[#2E453D] rounded-[2px] duration-200">
                      Simulator
                    </a>
                  </li>
                </>
              )}

              {/* RESOURCES Mobile Dropdown */}
              {item.key === "Resources" && (
                <>
                  <li className="group flex items-center justify-between">
                    <a href="/resources/trainings" className="block text-white text-[8px] py-1 px-1 w-full h-[20px] border-b-2 border-black h-[35px] flex justify-center items-center shadow shadow-xl bg-[#2E453D] rounded-[2px] duration-200">
                      Trainings & Learning
                    </a>
                  </li>
                  <li className="group flex items-center justify-between">
                    <a href="/resources/finance" className="block text-white text-[8px] py-1 px-1 w-full h-[20px] border-b-2 border-black h-[35px] flex justify-center items-center shadow shadow-xl bg-[#2E453D] rounded-[2px] duration-200">
                      Finance
                    </a>
                  </li>
                  <li className="group flex items-center justify-between">
                    <a href="/resources/guides" className="block text-white text-[8px] py-1 px-1 w-full h-[20px] border-b-2 border-black h-[35px] flex justify-center items-center shadow shadow-xl bg-[#2E453D] rounded-[2px] duration-200">
                      Guides/Instructions
                    </a>
                  </li>
                  <li className="group flex items-center justify-between">
                    <a href="/resources/programs" className="block text-white text-[8px] py-1 px-1 w-full h-[20px] border-b-2 border-black h-[35px] flex justify-center items-center shadow shadow-xl bg-[#2E453D] rounded-[2px] duration-200">
                      Programs
                    </a>
                  </li>
                  <li className="group flex items-center justify-between">
                    <a href="/resources/schedules" className="block text-white text-[8px] py-1 px-1 w-full h-[20px] border-b-2 border-black h-[35px] flex justify-center items-center shadow shadow-xl bg-[#2E453D] rounded-[2px] duration-200">
                      Schedules
                    </a>
                  </li>
                  <li className="group flex items-center justify-between">
                    <a href="/resources/development" className="block text-white text-[8px] py-1 px-1 w-full h-[20px] border-b-2 border-black h-[35px] flex justify-center items-center shadow shadow-xl bg-[#2E453D] rounded-[2px] duration-200">
                      Development
                    </a>
                  </li>
                </>
              )}

              {/* COMMUNICATION Mobile Dropdown */}
              {item.key === "Communication" && (
                <>
                  <li className="group flex items-center justify-between">
                    <a href="/communication/impact" className="block text-white text-[8px] py-1 px-1 w-full h-[20px] border-b-2 border-black h-[35px] flex justify-center items-center shadow shadow-xl bg-[#2E453D] rounded-[2px] duration-200">
                      Impact
                    </a>
                  </li>
                  <li className="group flex items-center justify-between">
                    <a href="/communication/environmental" className="block text-white text-[8px] py-1 px-1 w-full h-[20px] border-b-2 border-black h-[35px] flex justify-center items-center shadow shadow-xl bg-[#2E453D] rounded-[2px] duration-200">
                      Environmental (ESG)
                    </a>
                  </li>
                  <li className="group flex items-center justify-between">
                    <a href="/communication/social" className="block text-white text-[8px] py-1 px-1 w-full h-[20px] border-b-2 border-black h-[35px] flex justify-center items-center shadow shadow-xl bg-[#2E453D] rounded-[2px] duration-200">
                      Social (ESG)
                    </a>
                  </li>
                  <li className="group flex items-center justify-between">
                    <a href="/communication/governance" className="block text-white text-[8px] py-1 px-1 w-full h-[20px] border-b-2 border-black h-[35px] flex justify-center items-center shadow shadow-xl bg-[#2E453D] rounded-[2px] duration-200">
                      Governance (ESG)
                    </a>
                  </li>
                </>
              )}

            </ul>
          </div>
        </motion.div>
      )}
    </li>
  );

  const renderNotificationsButton = (isMobile = false) => (
    <li
      className="relative"
      onMouseEnter={!isMobile ? () => setHoveredItem("notifications") : undefined}
      onMouseLeave={!isMobile ? () => {
        if (!showNotifications) {
          setHoveredItem(null);
        }
      } : undefined}
    >
      <div 
        className={`
          h-[40px] md:h-[40px] flex items-center justify-between relative px-2 shadow-black shadow-md
          border-b-2 bg-[#2E453D] rounded-[2px] border-2 border-b-white
          ${(hoveredItem === "notifications" || showNotifications) ? "border-[#FDBD26] text-[#FDBD26]" : "border-transparent text-white"}
          w-[120px] ${isMobile ? "" : "xl:w-[190px] lg:w-[100px]"}
        `}
        onClick={() => {
          if (isMobile) {
            setShowNotifications(prev => !prev);
            if (!showNotifications) {
              setHoveredItem("notifications");
            } else {
              setHoveredItem(null);
            }
          } else {
            setShowNotifications(prev => !prev);
          }
        }}
      >
        <span className="block text-[10px] lg:text-[10px] xl:text-[14px] w-full text-center font-bold">
          Notifications
        </span>
        <div className="relative cursor-pointer md:flex hidden md:flex-shrink-0 ml-1">
          <img
            src="https://res.cloudinary.com/dwfdzdaxk/image/upload/v1752600948/image_1854_enk1t0.png"
            alt="bell"
            className="w-[10px] h-[10px] md:w-[20px] md:h-[20px]"
          />
          <span className="absolute -top-1 -right-1 bg-red-600 text-white text-[4px] md:text-[4px] w-2 h-2 rounded-full flex items-center justify-center">
            3
          </span>
        </div>
        <img 
          src="https://res.cloudinary.com/dwfdzdaxk/image/upload/v1753988748/Expand_right_light_dn8ljr.png" 
          alt="icon" 
          className={`w-[12px] h-[12px] transition-transform md:hidden flex ${showNotifications ? 'rotate-90' : ''}`}
        />
      </div>
      
      {/* Notification Dropdown */}
      <AnimatePresence>
        {(hoveredItem === "notifications" || showNotifications) && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className={`absolute top-full left-0 ${isMobile ? "w-[120px]" : "w-full xl:w-[160px] lg:w-[200px]"} z-50`}
            onClick={(e) => isMobile ? e.stopPropagation() : null}
          >
            <div className="mt-1">
              <div className={`absolute top-full left-0 ${isMobile ? "w-[120px]" : "w-full xl:w-[200px] lg:w-[200px]"}`}>
                <motion.div
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                  className={`${isMobile ? "w-full" : "right-0 w-[80px] md:w-[160px]"} shadow-xl z-50 overflow-hidden`}
                >
                  <div className="p-0 mt-3">
                    <div className="space-y-[2px] md:space-y-1">
                      {notificationItems.map((item) => (
                        <motion.div
                          key={item.key}
                          whileHover={{ scale: 1.02 }}
                          className={`bg-[#2E453D] p-1 md:p-2 flex flex-col justify-between transition-all h-[40px] md:h-[45px] duration-300 rounded-[10px] w-full ${
                            hoveredItem === item.key ? "border-b-[#FDBD26]" : "border-b-black border-b-4"
                          } border-b-2 shadow-lg`}
                          onMouseEnter={() => !isMobile && setHoveredItem(item.key)}
                          onMouseLeave={() => !isMobile && setHoveredItem("notifications")}
                          onClick={() => {
                            if (isMobile) {
                              setShowNotifications(false);
                              setHoveredItem(null);
                            }
                          }}
                        >
                          <div className="flex flex-col h-full">
                            <div className="flex justify-between items-center h-full w-full">
                              <span className="text-white text-center md:text-[14px] text-[10px] font-normal leading-tight flex-grow">
                                {item.label}
                              </span>
                              <div className="relative cursor-pointer flex-shrink-0 ml-2">
                                <img
                                  src="https://res.cloudinary.com/dwfdzdaxk/image/upload/v1752600948/image_1854_enk1t0.png"
                                  alt="bell"
                                  className="w-[15px] h-[15px] md:w-[20px] md:h-[20px]"
                                />
                                <span className="absolute -top-1 -right-1 bg-red-600 text-white text-[4px] md:text-[4px] w-2 h-2 rounded-full flex items-center justify-center">
                                  3
                                </span>
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </li>
  );

  return (
    <>
      <nav className="md:pr-12 md:pl-[110px] pt-2 md:pb-1 font-inter w-full mx-auto bg-transparent z-50 relative flex items-center justify-between md:justify-start md:space-x-2 px-2 md:px-0" ref={navRef}>
        {/* Logo & Chevron */}
        <div className="items-center md:flex hidden md:w-[120px]">
          <a href="/">
            <img
              src="https://res.cloudinary.com/dwfdzdaxk/image/upload/v1752629030/image_1778_jh4ypz.png"
              alt="Logo"
              className="xl:w-[150px] md:w-[67px] md:h-[67px] w-[80px] h-[40px] object-contain"
            />
          </a>
          {location.pathname !== "/newsmain" && (
            <div
              className={`relative transition-transform duration-500 ml-1`}
              onMouseEnter={() => setFlipped(true)}
              onMouseLeave={() => setFlipped(false)}
              ref={chevronRef}
            >
              <img
                src="https://res.cloudinary.com/dwfdzdaxk/image/upload/v1752628735/Expand_left_light_1_jbkf6v.png"
                alt="Chevron"
                className="cursor-pointer transition-transform hover:scale-110 md:w-[100px] md:h-[100px] w-[20px] h-[20px] object-contain"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  const { left, width } = e.target.getBoundingClientRect();
                  const clickPosition = e.clientX - left;
                  if (clickPosition < width / 2) {
                    handleNavigation("back");
                  } else {
                    handleNavigation("next");
                  }
                }}
              />
            </div>
          )}
        </div>

        {/* Desktop Menu */}
        <div className="hidden md:flex space-x-1 relative">
          <ul className="flex space-x-2 items-center">
            {menuItems.map((item) => renderNavItem(item))}
            {renderNotificationsButton()}
          </ul>
        </div>
        {/* Country Dropdown - Desktop */}
        <div className="hidden md:flex relative z-50 -pl-6">
          <button
            className="flex items-center space-x-4 text-[12px] text-gray-700 p-1 rounded-lg"
            onClick={() => setDropdownOpen(!dropdownOpen)}
          >
            <img
              src={selectedCountry.flag}
              alt={selectedCountry.name}
              className="md:w-[70px] md:h-[45px] w-6 h-4"
            />
            <BiSolidDownArrow className="text-white text-[12px]" />
          </button>
          {dropdownOpen && (
            <div className="absolute right-0 top-12 bg-[#2E453D] shadow-lg rounded-lg mt-1 w-48 z-50">
              <ul className="space-y-2 py-2">
                {countries.map((country, index) => (
                  <li
                    key={country.code}
                    className="flex items-center space-x-2 px-4 py-2 cursor-pointer hover:text-white hover:bg-[#20342d] opacity-0 translate-y-2 animate-slide-down"
                    onClick={() => handleCountryChange(country)}
                    style={{ animationDelay: `${index * 100}ms` }}
                  >
                    <img src={country.flag} alt={country.name} className="w-4 h-4" />
                    <span className="text-white hover:text-[#D2F34A] text-[12px]">
                      {country.name}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Mobile Header (Menu Toggle + Country + Notifications) */}
       {/* Mobile Header (Menu Toggle + Country + Notifications) */}
<div className="md:hidden flex flex-col gap-1 w-full">
  {/* First row - Logo, Country, Menu */}
  <div className="flex items-center justify-between w-full">
    <div className="flex items-center -gap-4">
      <a href="/">
        <img
          src="https://res.cloudinary.com/dwfdzdaxk/image/upload/v1752629030/image_1778_jh4ypz.png"
          alt="Logo"
          className="w-[20px] h-[20px] object-contain"
        />
      </a>
      <img
                src="https://res.cloudinary.com/dwfdzdaxk/image/upload/v1752628735/Expand_left_light_1_jbkf6v.png"
                alt="Chevron"
                className="cursor-pointer transition-transform hover:scale-110 md:w-[100px] md:h-[100px] w-[20px] h-[20px] object-contain"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  const { left, width } = e.target.getBoundingClientRect();
                  const clickPosition = e.clientX - left;
                  if (clickPosition < width / 2) {
                    handleNavigation("back");
                  } else {
                    handleNavigation("next");
                  }
                }}
              />
    </div>
    
    <div className="flex items-center gap-2">
      {/* Country Dropdown - Mobile */}
      <div className="relative z-50">
        <button
          className="flex items-center space-x-1 text-[12px] text-gray-700 p-1 rounded-lg"
          onClick={() => setDropdownOpen(!dropdownOpen)}
        >
          <img
            src={selectedCountry.flag}
            alt={selectedCountry.name}
            className="w-6 h-4"
          />
          <BiSolidDownArrow className="text-white text-[10px]" />
        </button>
      </div>
      
     {/* Mobile Menu Toggle */}
<button 
  className="flex items-center gap-1 z-50"
  onClick={() => setMenuOpen(!menuOpen)}
  aria-label="Toggle menu"
>
  {/* Hamburger icon that transforms to X when menu is open */}
  <div className="flex flex-col justify-between h-4 w-5">
    <span className={`w-full h-[2px] bg-white rounded-sm transition-all ${menuOpen ? "rotate-45 translate-y-1.5" : ""}`}></span>
    <span className={`w-full h-[2px] bg-white rounded-sm transition-all ${menuOpen ? "opacity-0" : ""}`}></span>
    <span className={`w-full h-[2px] bg-white rounded-sm transition-all ${menuOpen ? "-rotate-45 -translate-y-1.5" : ""}`}></span>
  </div>
  <span className="text-white text-[10px]">Menu</span>
</button>
    </div>
  </div>
  

</div>

    

        {/* Mobile Menu */}
        <AnimatePresence>
          {menuOpen && (
            <motion.div
              initial={{ opacity: 0, x: '100%' }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: '100%' }}
              transition={{ duration: 0.3 }}
              className="md:hidden fixed inset-0 z-40"
              onClick={() => setMenuOpen(false)}
            >
              <motion.div 
                className="absolute top-8 left-32 w-full h-full shadow-xl z-50 p-2 overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
                initial={{ x: '100%' }}
                animate={{ x: 0 }}
                exit={{ x: '100%' }}
                transition={{ duration: 0.3 }}
              >
               
                
                <ul className="space-y-1">
                  {menuItems.map((item) => renderMobileNavItem(item))}
                  <li className="mt-4">
                    {renderNotificationsButton(true)}
                  </li>
                </ul>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
      
      {showPopup && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          className="fixed bottom-4 right-4 bg-[#2E453D] text-white px-4 py-2 rounded-lg shadow-lg z-50 flex items-center space-x-2 border border-[#FDBD26] max-w-xs text-sm"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 text-[#FDBD26] flex-shrink-0"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <span className="text-xs sm:text-sm">{popupMessage}</span>
        </motion.div>
      )}
    </>
  );
};

export default Navbar;